<script lang="ts">
	// Svelte stores
	import { format } from 'svelte-i18n';
	import { get } from 'svelte/store';
	// Stores
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices_config } from 'src/configs/devices';
	// Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_control_component_channel_structure,
		get_mode,
		is_gen_2,
		real_id,
		isXMODDevice,
	} from 'src/helpers/interface_helpers';
	import { hasAddonPeripherals, getAddonStatus } from 'src/helpers/addons/addon_helpers';
	import isDimmerWithCalibration from 'src/helpers/is_calibratable_dimmer';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { getCustomizeLabelList } from 'src/components/DevicesSettings/CustomizeDeviceWizard/customize-device-wizard-helpers';
	import {
		setupGen1TimerLogic,
		createGen1StatusLabels,
		createGen1CombinedLabelList,
		getGen1DeviceStatuses,
	} from 'src/components/Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/Devices/Shelly/Gen1/gen1-main-helpers';
	// Design System Components
	import { Alert } from '@Shelly/shelly-design-system';
	// Components
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import ControlSliderV2 from 'src/components/Controls/Single/ControlSliderV2.svelte';
	import ControlOnOff from 'src/components/Controls/Single/ControlOnOff.svelte';
	import ControlOnOffAddon from 'src/components/Controls/Single/ControlOnOffAddon.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	import type { Device } from 'src/types/Device';

	export let device: Device;

	const deviceConfig = devices_config?.[device?.type];
	const isGen2 = is_gen_2(device);
	const _format = get(format);

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;

	$: hasAllData = !!(
		device &&
		status !== undefined &&
		settings !== undefined &&
		deviceConfig !== undefined
	);

	$: mode = get_mode(device, status, settings);

	// Status and label processing - use Gen1 helpers for Gen1 devices to avoid status_configurator errors
	$: _statusesGen2 =
		isGen2 && status?.online && deviceConfig ? status_configurator(status, device, settings) : [];
	$: _statusesGen1 = !isGen2 ? getGen1DeviceStatuses(status, device, settings, deviceConfig) : [];

	$: labelListGen2 = Array.isArray(_statusesGen2)
		? device?.statusLabelParam
			? getCustomizeLabelList(device, _statusesGen2)
			: _statusesGen2.filter((e) => {
					return 'undefined' === typeof e.main && e.name !== 'state';
				})
		: [];
	$: labelListGen1 = !isGen2 ? createGen1StatusLabels(_statusesGen1, device) : [];

	$: combinedLabelListGen2 = [...Object.values(timerLabels), ...labelListGen2];
	$: combinedLabelListGen1 = createGen1CombinedLabelList(timerLabels, labelListGen1);
	$: combinedLabelList = isGen2 ? combinedLabelListGen2 : combinedLabelListGen1;

	let timerLabels: Record<string, any> = {};

	$: timer =
		status?.online && deviceConfig && isGen2
			? status?.[`${get_gen_2_component_channel_structure(device)}`]?.timer_started_at
			: false;

	$: gen1Timer =
		hasAllData && status?.online && !isGen2 ? setupGen1TimerLogic(device, status, false) : false;

	// Handle slider prop and white slider based on device mode (matching DeviceDrawer logic)
	$: sliderProp = (() => {
		if (!status?.online || !deviceConfig) return '';
		if (isXMODDevice(device?.type)) {
			// For XMOD devices, check status for component channels
			const hasColorControl = status?.['rgbw:0'] && device?.mode === 'rgbw';
			const hasColorTempControl = status?.['cct:0'] && device?.mode === 'cct';
			const hasLightControl = status?.['light:0'] && device?.mode === 'light';
			return hasColorControl || hasColorTempControl || hasLightControl ? 'brightness' : '';
		}
		// For non-XMOD devices, use device config
		return devices_config?.[device?.type]?.modes?.[mode]?.slider || '';
	})();

	$: hasWhiteSlider =
		status?.online && deviceConfig && !isXMODDevice(device?.type)
			? devices_config?.[device?.type]?.modes?.[mode]?.has_white || false
			: false;

	// Calibration-related state (mirrors DeviceDrawer logic for Gen2 dimmers)
	$: componentPath = isGen2 ? get_gen_2_component_channel_structure(device) : '';
	$: flags = componentPath && status ? status?.[componentPath]?.flags || [] : [];
	$: isCalibratableDimmer = isGen2 && isDimmerWithCalibration(device?.type);
	$: isUncalibrated = Array.isArray(flags) && flags.includes('uncalibrated');
	$: showChannelCalibrationAlert = isUncalibrated && mode !== 'light';
	$: showDeviceCalibrationAlert = isUncalibrated && isCalibratableDimmer && mode === 'light';

	// Gate sliders when dimmer is uncalibrated for calibratable devices
	$: shouldShowSliders = status?.online && deviceConfig && (!isCalibratableDimmer || !isUncalibrated);

	// Add-on peripherals (for detecting switch add-on)
	$: addonPeripherals = hasAddonPeripherals(device) ? getAddonStatus(device, [], false, true) : [];
	$: switchAddon = addonPeripherals.find((p) => p.peripheralType === 'switch');
</script>

<DeviceViewStateWrapper {device}>
	<div class="label-list-wrapper mb-s" slot="staticTop">
		{#if hasAllData && status?.online}
			{#if isGen2 && timer}
				{#key status[get_gen_2_control_component_channel_structure(device)].timer_started_at}
					<TimerRemaining timerKey="gen2_main" {device} {status} {settings} bind:timerLabels />
				{/key}
			{:else if !isGen2 && gen1Timer && status?.[`${device.category}s`]?.[device.channel]?.timer_started}
				{#key status[`${device.category}s`][device.channel].timer_started}
					<TimerRemaining timerKey="gen1_main" {device} {status} {settings} bind:timerLabels />
				{/key}
			{/if}
			<DeviceLabelList labelList={combinedLabelList} />
			<DeviceNotificationLabel deviceId={device?.id} />
		{/if}
	</div>

	<!-- Light controls - handles RGBW modes with white channel slider when needed -->
	<div class="flex-full-height min-height-0 justify-content-end" slot="content">
		{#if hasAllData && status?.online && shouldShowSliders}
			{#if hasWhiteSlider}
				<!-- Show white channel slider (horizontal) when mode supports it -->
				<ControlSliderV2
					{device}
					{status}
					{settings}
					prop="white"
					type="horizontal"
					needsRanging
					horizontalSliderClass="mb-l"
				/>
			{/if}
			{#if sliderProp}
				<!-- Show brightness slider (horizontal if hasWhiteSlider, vertical otherwise) -->
				<ControlSliderV2
					{device}
					{status}
					{settings}
					prop={sliderProp}
					type={hasWhiteSlider ? 'horizontal' : 'vertical'}
					horizontalSliderClass="mb-s-plus"
				/>
			{/if}
		{/if}
	</div>
	<!-- staticBottom content -->

	<div class="flex-full-height justify-content-end" slot="staticBottom">
		{#if hasAllData && status?.online && (showChannelCalibrationAlert || showDeviceCalibrationAlert)}
			<div class="mb-s max-width-full">
				{#if showChannelCalibrationAlert}
					<Alert
						class="max-width-full"
						heading={_format('alert_heading_non_calibrated_dimmer', {
							default: `Non calibrated channel.`,
						})}
						message={_format('description_dimmer_calibration', {
							default:
								"Calibrate current dimmer's channel. Note: During calibration you can't calibrate other channel. During calibration don't change current channel state.",
						})}
						isHeadingBold
						cta={{
							title: _format('button_calibrate', {
								default: 'calibrate',
							}),
						}}
						on:cta_click={() => {
							navigateToSetting(real_id(device.id), 'settings', 'calibration');
						}}
					/>
				{:else if showDeviceCalibrationAlert}
					<Alert
						class="max-width-full"
						heading={_format('alert_device_not_calibrated', {
							default: `Your device is not calibrated.`,
						})}
						message={_format('info_roller_calibrate_device', {
							default: 'Please calibrate your device for the correct attitude!',
						})}
						isHeadingBold
						cta={{
							title: !isSubAccount()
								? _format('button_calibrate', {
										default: 'calibrate',
									})
								: '',
						}}
						on:cta_click={() => {
							navigateToSetting(real_id(device.id), 'settings', 'calibration');
						}}
					/>
				{/if}
			</div>
		{/if}

		{#if switchAddon}
			<div class="d-flex gap-s">
				<div class="control-flex-column">
					<ControlOnOffAddon
						{device}
						{status}
						isInSlot
						component={switchAddon.sensor?.component}
						useV2Components
						deviceButtonV2Props={{ size: 'm' }}
					/>
				</div>
				<div class="control-flex-column">
					<ControlOnOff
						{device}
						{status}
						{settings}
						isInSlot
						useV2Components
						deviceButtonV2Props={{ size: 'm' }}
					/>
				</div>
			</div>
		{:else}
			<ControlOnOff
				{device}
				{status}
				{settings}
				isWide
				useV2Components
				deviceButtonV2Props={{ size: 'l' }}
			/>
		{/if}
	</div>
</DeviceViewStateWrapper>

<style>
	.control-flex-column {
		flex: 1;
	}
</style>
